const roundMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

const getPaymentName = (payment) =>
  payment?.medioDePago?.nombre || payment?.nombre || "Medio de pago";

const isRejectedPayment = (payment) => normalizeText(payment?.estado) === "RECHAZADO";

export const isTransferPayment = (payment) =>
  normalizeText(getPaymentName(payment)) === "TRANSFERENCIA";

export const getVoucherName = (venta) =>
  venta?.voucher?.grupo?.nombre || venta?.voucher?.nombreVoucher || "Beneficio/Voucher";

export const getPedidoPaymentInfo = (pedido = {}) => {
  const venta = pedido?.venta || {};
  const pagos = (Array.isArray(venta?.pagos) ? venta.pagos : []).filter(
    (pago) => !isRejectedPayment(pago)
  );
  const transferencias = pagos.filter(isTransferPayment);
  const pagoSplitCC = roundMoney(pedido?.pagoSplitCC);
  const tieneVoucher = Boolean(venta?.idVoucher);
  const tieneTransferencia = transferencias.length > 0;
  const tieneCuentaCorriente = Boolean(pedido?.cuentaCorriente) || pagoSplitCC > 0;
  const esSplitSinVoucher = !tieneVoucher && pagoSplitCC > 0 && tieneTransferencia;

  let etiqueta;
  let tipo;
  if (tieneVoucher && tieneCuentaCorriente) {
    etiqueta = "Voucher / Cuenta corriente";
    tipo = "VOUCHER_CUENTA_CORRIENTE";
  } else if (tieneVoucher && tieneTransferencia) {
    etiqueta = "Voucher / Transferencia";
    tipo = "VOUCHER_TRANSFERENCIA";
  } else if (tieneVoucher) {
    etiqueta = `Voucher: ${getVoucherName(venta)}`;
    tipo = "VOUCHER";
  } else if (esSplitSinVoucher) {
    etiqueta = "Transferencia / Cuenta corriente";
    tipo = "TRANSFERENCIA_CUENTA_CORRIENTE";
  } else if (pedido?.cuentaCorriente) {
    etiqueta = "Cuenta corriente";
    tipo = "CUENTA_CORRIENTE";
  } else if (pagos.length > 0) {
    etiqueta = [...new Set(pagos.map(getPaymentName))].join(" / ");
    tipo = tieneTransferencia ? "TRANSFERENCIA" : "OTROS_PAGOS";
  } else {
    etiqueta = pedido?.estado === "CERRADO" ? "Sin información" : "-";
    tipo = "SIN_INFORMACION";
  }

  const subtotal = roundMoney(venta?.subtotal ?? pedido?.total);
  const descuento = roundMoney(venta?.descuento);
  const recargo = roundMoney(venta?.recargo);
  const remanente = roundMoney(venta?.total ?? Math.max(subtotal + recargo - descuento, 0));
  const montoCuentaCorriente = tieneCuentaCorriente
    ? roundMoney(pagoSplitCC || (pedido?.cuentaCorriente ? remanente : 0))
    : 0;
  const montoTransferencia = roundMoney(
    transferencias.reduce((total, pago) => total + roundMoney(pago?.totalPago), 0)
  );
  const mediosPago = pagos.map((pago) => ({
    nombre: getPaymentName(pago),
    monto: roundMoney(pago?.totalPago),
    estado: pago?.estado || null,
    esTransferencia: isTransferPayment(pago),
  }));

  return {
    tipo,
    etiqueta,
    pagos,
    tieneVoucher,
    tieneTransferencia,
    tieneCuentaCorriente,
    esSplitSinVoucher,
    voucherNombre: tieneVoucher ? getVoucherName(venta) : null,
    subtotal,
    descuento,
    recargo,
    remanente,
    montoCuentaCorriente,
    montoTransferencia,
    mediosPago,
  };
};

export { roundMoney };
